import { Form, Space } from 'antd';
import dayjs from 'dayjs';
import { memo, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { EyeIcon } from 'assets/svg';
import ButtonAction from 'components/Button/ButtonAction';
import TableComponent from 'components/Table/TableComponent';
import { useUser } from 'contexts/UserProvider';
import { IUserApproval, IUserApprovalData } from 'interfaces/Approve/Approve';
import { ColumnsTypeCustom } from 'interfaces/Table/ColumnType';
import Content from 'layouts/Content';
import path from 'routes/Path';
import { HocChangePagination } from 'utils/HocChangePagination';
import { icons } from 'utils/constants/icons';

interface IProps {
  loading?: boolean;
  data: IUserApprovalData;
  onApprove: (data: IUserApproval) => void;
  onCorrectionRequest: (data: IUserApproval) => void;
}

const { CloseOutlined, CheckOutlined } = icons;

const TableApprovePage = ({
  data,
  loading,
  onApprove,
  onCorrectionRequest,
}: IProps) => {
  const { user } = useUser();
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const getStatusColor = useCallback((statusId: string) => {
    switch (statusId) {
      case 'AP100_01':
        return { color: '#0077CC' };
      case 'AP100_02':
        return { color: '#038F00' };
      case 'AP100_03':
        return { color: '#DC362E' };
      case 'AP100_04':
        return { color: '#757779' };
      default:
        return { color: '#000000' };
    }
  }, []);

  const handleNavigate = useCallback(
    (approvalId: string, state: IUserApproval) => {
      switch (approvalId) {
        case '1':
          navigate(path.APPROVE, { state });
          break;
        case '2':
          navigate(path.APPROVE, { state });
          break;
        case '3':
          // navigate(path.APPROVE_PRICE_ORDER, { state });
          navigate(`${path.APPROVE_PRICE_ORDER}?id=${state.id}`);
          break;
        case '4':
          // navigate(path.APPROVE_PRICE_PURCHASER, { state });
          navigate(`${path.APPROVE_PRICE_PURCHASER}?id=${state.id}`);
          break;
        case '5':
          navigate(path.APPROVE, { state });
          break;
        case '6':
          // navigate(path.APPROVE_NEW_CUSTOMER, { state });
          navigate(`${path.APPROVE_NEW_CUSTOMER}?id=${state.id}`);
          break;
        case '7':
          navigate(path.APPROVE, { state });
          break;
        default:
          navigate(path.APPROVE, { state });
          break;
      }
    },
    [],
  );

  const columns: ColumnsTypeCustom = useMemo(
    () => [
      {
        title: 'STT',
        width: 50,
        render: (_, __, index: number) =>
          data.pageInfo?.itemsPerPage * (data.pageInfo?.currentPage - 1) +
          index +
          1,
      },
      {
        title: 'Mã phê duyệt',
        dataIndex: 'id',
        width: 135,
      },
      {
        title: 'Loại phê duyệt',
        dataIndex: ['tapproval', 'name'],
        width: 270,
      },
      {
        title: 'Trạng thái',
        dataIndex: ['statusApproval'],
        width: 120,
        render: (id: string, record: any) => (
          <span style={getStatusColor(id)}>
            {record?.tstatusApproval?.name}
          </span>
        ),
      },
      {
        title: 'Người gửi duyệt',
        dataIndex: ['tcreateUser', 'fullName'],
        width: 145,
      },
      {
        title: 'Chi nhánh',
        dataIndex: 'plantId',
        className: 'text-left',
        width: 141,
      },
      {
        title: 'Ngày gửi yêu cầu',
        dataIndex: 'createDate',
        width: 170,
        render: (value: any) => dayjs(value).format('DD/MM/YYYY HH:mm:ss'),
      },
      {
        width: 150,
        fixed: 'right',
        align: 'center',
        render: (record: IUserApproval) => {
          const isApproved =
            record?.tuserApprovalDetail?.find(
              (item) => item?.email === user?.email,
            )?.approvalYn !== null;

          const isDisabled =
            isApproved ||
            record.statusApproval !== 'AP100_01' ||
            !record?.tuserApprovalDetail?.filter(
              (item) => item?.email === user?.email,
            )?.length;

          return (
            <Space size="middle">
              <ButtonAction
                tooltip={'Xem chi tiết'}
                onClick={() => handleNavigate(record.approvalId, record)}
              >
                <EyeIcon />
              </ButtonAction>
              <ButtonAction
                tooltip="Duyệt"
                disabled={isDisabled}
                onClick={() => onApprove(record)}
              >
                {isDisabled ? (
                  <CheckOutlined />
                ) : (
                  <CheckOutlined className="[&>svg>path]:fill-[#038F00]" />
                )}
              </ButtonAction>
              <ButtonAction
                tooltip="Từ chối"
                disabled={isDisabled}
                onClick={() => onCorrectionRequest(record)}
              >
                {isDisabled ? (
                  <CloseOutlined />
                ) : (
                  <CloseOutlined className="[&>svg>path]:fill-[#DC362E]" />
                )}
              </ButtonAction>
            </Space>
          );
        },
      },
    ],
    [data, user],
  );

  return (
    <Content>
      <Form form={form}>
        <TableComponent
          columns={columns}
          loading={loading}
          dataSource={data.items}
          scroll={{ x: 'max-content' }}
          pagination={{
            current: data.pageInfo?.currentPage,
            pageSize: data.pageInfo?.itemsPerPage,
            total: data.pageInfo?.totalItems,
            onChange: HocChangePagination(),
          }}
        />
      </Form>
    </Content>
  );
};

export default memo(TableApprovePage);
